import classNames from 'classnames'

import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import {
  AIRecommendProductShortTitleType,
  LinkDetailType,
  ProductTemplate,
} from 'service/list'
import { Me } from 'service/user'
import { FormValues } from 'utils/type'
import { SelectionModal } from 'components/layout/SelectionModal'
import { ReactComponent as StarIcon } from 'svg/star_white.svg'
import { TextArea } from 'components/atom/TextArea'
import { THUMBNAIL_PATTERN } from 'utils/common'
import TemplateTooltip from 'pages/content-video-editor/components/shortvideo/TemplateTooltip'
import { CustomImage } from 'pages/content-video-editor/components/Image'
import HoverVideo from 'pages/content-video-editor/components/HoverVideo'
import { CircularCheckbox } from 'components/CircularCheckbox'
import { PreviewTag } from 'components/tag/PreviewTag'
import { createLogger } from 'utils/logger'
import { PreviewGuideTooltip } from 'pages/product-video-editor/components/video/PreviewGuideTooltip'
import useGetAiRecommendProductShortTitle from 'hooks/query/extension/useGetAiRecommendProductShortTitle'
interface TemplateSelectorSelectorProps {
  watch: UseFormWatch<FormValues>
  setValue: UseFormSetValue<FormValues>
  control: Control<FormValues, any>
  translatePlanName?: (planName: string) => string
  userInfo: Me
  templateList: ProductTemplate | undefined
  productIndex?: number
  basicTitle: string
  selectedLink: LinkDetailType
}

const logger = createLogger('templateSelector')
export const TemplateSelector = ({
  watch,
  setValue,
  control,
  translatePlanName,
  userInfo,
  templateList,
  productIndex = 0,
  basicTitle,
  selectedLink,
}: PropsWithChildren<TemplateSelectorSelectorProps>) => {
  const { mutateAsync: shortTitleMutateAsync, isPending: shortTitleIsPending } =
    useGetAiRecommendProductShortTitle()

  const [aiRecommendProductShortTitle, setAiRecommendProductShortTitle] =
    useState<AIRecommendProductShortTitleType | null>(null)

  const withShort = watch(`products.${productIndex}.withShort`)
  const [placidTemplateList, setPlacidTemplateList] = useState<any>(
    templateList?.placid
  )

  const [isPreviewGuideOpen, setPreviewGuideOpen] = useState(false)

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [templateTooltip, setTemplateTooltip] = useState<boolean>(false)
  const [isUserWrite, setIsUserWrite] = useState<boolean | null>(false)

  const [selectedImagePath, setSelectedImagePath] = useState<string>('')
  const [splitTitles, setSpiltTitles] = useState<string[] | null>(null)

  const handleMutate = async () => {
    const res = await shortTitleMutateAsync([selectedLink.id])
    setAiRecommendProductShortTitle(res)
  }
  const handleSaveBtn = () => {
    /**
     * 1.templateTitle length !== 0
     * 2. templateId !== 0
     * 위에 확인후 조건이 맞을시
     * 3. setIsModalOpen === false
     */

    const templateTitleLen = watch(
      `products.${productIndex}.productTitle`
    ).length
    const templateId = watch(`products.${productIndex}.productTemplateId`)

    if (templateTitleLen !== 0 && templateId !== 0) {
      return setIsModalOpen(false)
    }
  }

  const defaultTemplate = useMemo(() => {
    return templateList?.placid.list.find((el) => el.default === 1)
  }, [templateList]) // 의존성 배열 추가!

  const handleBasicTitle = (value: boolean) => {
    setIsUserWrite(value)
  }
  useEffect(() => {
    if (templateList?.placid) setPlacidTemplateList(templateList.placid)
  }, [templateList])

  useEffect(() => {
    if (aiRecommendProductShortTitle) {
      setSpiltTitles(aiRecommendProductShortTitle.youtubeTitlesSplit)
      // setValue(`products.${productIndex}.productTitle`, thumbnailTitlesSplit[0])
      // logger.info('thumbnailTitlesSplit', {
      //   thumbnailTitlesSplit: thumbnailTitlesSplit[0],
      // })
    }
  }, [aiRecommendProductShortTitle])

  useEffect(() => {
    if (defaultTemplate) {
      setValue(`products.${productIndex}.productTemplateId`, defaultTemplate.id)
      setSelectedImagePath(defaultTemplate.path)
    }
  }, [defaultTemplate])

  useEffect(() => {
    if (isUserWrite) {
      setValue(`products.${productIndex}.productTitle`, '')
      //기존 선택했던 템플릿이 있으면 선택
      const selectedTemplateId = watch(
        `products.${productIndex}.saveProductTemplateId`
      )
      if (templateList) {
        const selectedTemplate = placidTemplateList?.list.find(
          (el: any) => el.id === selectedTemplateId
        )
        if (selectedTemplate) {
          setValue(
            `products.${productIndex}.productTemplateId`,
            selectedTemplate?.id
          )
          setSelectedImagePath(selectedTemplate.path)
        } else if (defaultTemplate) {
          setValue(
            `products.${productIndex}.productTemplateId`,
            defaultTemplate?.id
          )
          setSelectedImagePath(defaultTemplate.path)
        }
      }
      setValue(
        `products.${productIndex}.productTitle`,
        watch(`products.${productIndex}.saveProductTitle`) ?? ''
      )
    } else {
      //기본선택하기
      setValue(`products.${productIndex}.productTitle`, basicTitle ?? '')
      if (defaultTemplate) {
        setValue(
          `products.${productIndex}.productTemplateId`,
          defaultTemplate?.id
        )
        setSelectedImagePath(defaultTemplate.path)
      }
    }
  }, [isUserWrite])
  useEffect(() => {
    ;(async () => {
      logger.info('handleMutate')
      await handleMutate()
    })()
  }, [])
  if (withShort === 0) return null

  return (
    <>
      <div className="content_layout_selector">
        <div className=" title flex flex-col gap-2 font-bold text-[18px] shrink-0 mb-4">
          <p className="flex font-bold text-[18px] relative items-center gap-[6px]  shrink-0">
            상품{productIndex + 1} 숏폼 영상 제목 & 화면 구성
          </p>

          <div className="grid md:grid-cols-2 grid-cols-1 md:gap-y-0 gap-y-2">
            {[
              { name: '기본(상품명이 제목이 됩니다)', value: false },
              { name: '내가 직접 정하기', value: true },
            ].map((el, templateIndex) => {
              return (
                <div
                  key={templateIndex}
                  className="font-[400] flex items-center gap-2"
                >
                  <label
                    onClick={(e) => handleBasicTitle(el.value)}
                    className=" cursor-pointer"
                  >
                    <CircularCheckbox
                      id={templateIndex}
                      checked={isUserWrite === el.value}
                      label={el.name}
                      onChange={() => {}}
                    />
                  </label>
                  {templateIndex === 0 && (
                    <PreviewTag setIsOpenModal={setPreviewGuideOpen} />
                  )}
                  {isPreviewGuideOpen && (
                    <PreviewGuideTooltip
                      setIsOpenPurchaseInfoGuideModal={setPreviewGuideOpen}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div
          className={classNames('', {
            'flex items-center gap-3 ': !isMobile,
            ' relative': isUserWrite,
          })}
        >
          {isUserWrite && (
            <div
              className={classNames('rounded-[10px] px-4', {
                'selector_list flex  gap-2 items-center bg-[#F6F8FB] min-h-[116px] w-full justify-between':
                  !isMobile,
                'selector_list flex items-center  justify-between gap-2  py-4 bg-[#F6F8FB] min-h-[116px] w-full':
                  isMobile,
              })}
            >
              <div
                className={classNames('', {
                  'flex gap-5 ': !isMobile,
                  'grid grid-col-1 gap-y-3': isMobile,
                })}
              >
                <div className="flex gap-1 items-center">
                  <p className="font-bold text-[14px]  text-[#8F929B] px-1 py-[2px] bg-[#D9DEE8] rounded-[4px]">
                    영상 제목
                  </p>
                  <p className="min-w-[143px] min-h-[48px] px-2 py-1 rounded-[4px] bg-[#EEF1F7]">
                    {watch(`products.${productIndex}.productTitle`)
                      ? watch(`products.${productIndex}.productTitle`)
                          ?.split('\n')
                          .map((el) => (
                            <>
                              {el} <br />
                            </>
                          ))
                      : '제목을 선택해주세요!'}
                  </p>
                </div>
                <div className="flex gap-1 items-center">
                  <p className="font-bold text-[14px]  text-[#8F929B] px-1 py-[2px] bg-[#D9DEE8] rounded-[4px] ">
                    화면 구성
                  </p>

                  {selectedImagePath && (
                    <p className="w-[57px] h-[100px] ">
                      <CustomImage
                        path={selectedImagePath}
                        width={57}
                        height={100}
                      />
                    </p>
                  )}
                </div>
              </div>
              <button
                className="px-2 py-[6px] bg-[#2E343D] text-[white] rounded-[4px] text-[14px]"
                onClick={() => {
                  setIsModalOpen(true)
                }}
                type="button"
              >
                선택 {'>'}
              </button>
            </div>
          )}
        </div>
      </div>
      <SelectionModal isModal={isModalOpen}>
        {
          <div
            className={classNames('', {
              'bg-white md:w-[816px] h-auto  shadow-main rounded-t-[20px] px-8 pt-8  pb-44 flex flex-col   gap-8':
                !isMobile,
              'bg-white w-full  shadow-main   flex flex-col   gap-8 pt-10 px-4  ':
                isMobile,
            })}
          >
            <div className="title flex flex-col  gap-4  ">
              <p className="flex justify-between items-center">
                <span className="font-bold text-[24px] text-left">
                  영상 제목 + 화면 구성
                </span>
                {/* <button
                  onClick={() => {
                    setIsModalOpen(false)
                  }}
                  className=" flex items-center"
                >
                  <img
                    src="assets/img/Icon_x.png"
                    alt="icon_x"
                    height={40}
                    width={40}
                  />
                </button> */}
              </p>
              {/* <div className="flex gap-2 items-center">
                <img
                  src="assets/img/cash.png"
                  alt="point"
                  width={11}
                  height={13}
                />

                <span className="text-main">30 캐시</span>
              </div> */}
            </div>
            {/* ai title input */}
            <div className="flex flex-col items-start gap-4">
              <div className="flex flex-col items-start">
                <p className="font-bold text-[18px] flex items-center gap-2 relative">
                  영상용 제목 입력{' '}
                  <TemplateTooltip
                    templateTooltip={templateTooltip}
                    setTemplateTooltip={setTemplateTooltip}
                  />
                </p>
                <p className="text-main text-[14px]">
                  영상 상단에 들어갈 제목입니다.
                </p>
              </div>
              <p
                className={classNames(
                  ' py-1 rounded-[4px] inline-flex gap-2 items-center'
                )}
              >
                <span className="text-[12px] bg-[#8F929B] px-1 py-[2px] text-white shrink-0 rounded-[4px] font-bold">
                  상품명
                </span>
                {watch(`products.${productIndex}`)?.productName ?? '--'}
              </p>
              <div className="ai title list flex flex-col gap-2 w-full">
                <span className="bg-black text-white text-[12px]  inline-flex gap-1 rounded-[4px] p-1 font-bold w-[90px]">
                  <StarIcon />
                  AI 추천 제목
                </span>
                <div className="flex gap-3 flex-wrap">
                  {splitTitles?.map((el, index) => (
                    <button
                      className={classNames(
                        'px-2 py-1 rounded-[4px]  text-left',
                        {
                          'bg-[#EEF1F7]   hover:bg-[#BBEBEC]':
                            watch(`products.${productIndex}.productTitle`) !==
                            el,
                          'bg-[#BBEBEC] ':
                            watch(`products.${productIndex}.productTitle`) ===
                            el,
                        }
                      )}
                      type="button"
                      onClick={() => {
                        setValue(`products.${productIndex}.productTitle`, el)
                        setValue(
                          `products.${productIndex}.saveProductTitle`,
                          el
                        )
                      }}
                    >
                      {el.split('\n').map((el) => (
                        <>
                          {el} <br />
                        </>
                      ))}
                    </button>
                  ))}
                </div>
                <div className=" flex flex-col gap-2 items-start">
                  <Controller
                    key={`products.${productIndex}.productTitle`}
                    render={({ field }) => {
                      const handleInput = (
                        e: React.ChangeEvent<HTMLTextAreaElement>
                      ) => {
                        const value = e.target.value
                        const lines = value?.split('\n').length

                        if (lines > 3) {
                          const trimmedValue = value
                            .split('\n')
                            .slice(0, 4)
                            .join('\n')

                          field.onChange(trimmedValue) // 강제로 잘라서 저장
                        } else {
                          field.onChange(value) // 정상 입력
                        }
                      }
                      return (
                        <>
                          {
                            <>
                              <TextArea
                                {...field}
                                // error={watch('templateTitle').length === 0}
                                maxLength={26}
                                placeholder={
                                  '한 줄에 10자가 넘지 않게 제목을 입력해야\n화면 구성에 잘 어울려요!'
                                }
                                onChange={(e: any) => {
                                  const value = e.target.value
                                  setValue(
                                    `products.${productIndex}.productTitle`,
                                    value
                                  )
                                  setValue(
                                    `products.${productIndex}.saveProductTitle`,
                                    value
                                  )
                                }}
                                width="w-full"
                                onInput={handleInput}
                              />
                            </>
                          }
                          <p
                            className={`flex gap-[4px] mb-1 ${
                              !THUMBNAIL_PATTERN.test(field.value) &&
                              'text-error'
                            }`}
                          >
                            <span className="text-left">
                              {field.value?.length}/26{' '}
                              {`이모지는 허용되지 않습니다.`}
                              (한 줄에 10자가 넘지 않게 제목을 입력해야 화면
                              구성에 잘 어울려요!)
                            </span>
                          </p>
                        </>
                      )
                    }}
                    name={`products.${productIndex}.productTitle`}
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: '영상 제목은 필수입니다!',
                      },
                      validate: (value) => {
                        if (!value || value?.trim() === '') {
                          return '영상 제목은 필수입니다!'
                        }
                        if (!THUMBNAIL_PATTERN.test(value)) {
                          return '이모지는 허용되지 않습니다.'
                        }
                        return true
                      },
                    }}
                  />
                </div>
              </div>
            </div>
            {/* template list */}
            <div className="template_list flex flex-col gap-4 items-start">
              <p className="font-bold text-[18px]">화면 구성 </p>
              <div
                className={classNames('', {
                  ' grid grid-cols-5 gap-[14px]': !isMobile,
                  ' grid grid-cols-2 gap-[26px] px-8 w-full': isMobile,
                })}
              >
                {placidTemplateList?.list?.map((el: any, index: number) => (
                  <div key={el?.id || index}>
                    <Controller
                      render={({ field }: any) => (
                        <div
                          className="relative cursor-pointer"
                          onClick={() => {
                            setValue(
                              `products.${productIndex}.productTemplateId`,
                              el?.id
                            )
                            setValue(
                              `products.${productIndex}.saveProductTemplateId`,
                              el?.id
                            )
                            setSelectedImagePath(el?.path)
                          }}
                        >
                          <div className="absolute right-2 bottom-2 ">
                            <input
                              type="checkbox"
                              className="sr-only"
                              onChange={() => {}}
                            />
                            <div
                              className={`w-[20px] h-[20px] rounded-full border-2 bg-main flex items-center justify-center ${
                                watch(
                                  `products.${productIndex}.productTemplateId`
                                ) === el?.id
                                  ? 'bg-white border-main'
                                  : 'bg-white'
                              }`}
                            >
                              {watch(
                                `products.${productIndex}.productTemplateId`
                              ) === el?.id && (
                                <div className="w-3 h-3 bg-main rounded-full"></div>
                              )}
                            </div>
                          </div>
                          <HoverVideo
                            videoPath={el?.videoPath}
                            path={el?.path}
                            index={index}
                            onlyImg={true}
                          />
                        </div>
                      )}
                      name={`products.${productIndex}.productTemplateId`}
                      control={control}
                    />
                  </div>
                ))}
              </div>
            </div>
            {isMobile && (
              <div className=" pb-20">
                <button
                  className="bg-main rounded-full w-full text-white font-bold h-[60px]  z-50  disabled:opacity-40"
                  type="button"
                  onClick={handleSaveBtn}
                  //   disabled={
                  //     watch('templateId') === 0 ||
                  //     watch('templateTitle').length === 0
                  //   }
                >
                  저장
                </button>
              </div>
            )}
          </div>
        }
        {!isMobile && (
          <div
            className="sticky bottom-0 p-8 shadow-main w-full md:w-[816px] bg-white rounded-b-[20px] 
                before:content-[''] before:absolute before:top-[-10px] before:left-0 
                before:w-full before:h-[10px] before:bg-gradient-to-t before:from-black/10 before:to-transparent z-50"
          >
            <button
              className="bg-main rounded-full w-full text-white font-bold h-[60px]  z-50 disabled:opacity-40"
              type="button"
              onClick={handleSaveBtn}
              //   disabled={
              //     watch('templateId') === 0 || watch('templateTitle').length === 0
              //   }
            >
              저장
            </button>
          </div>
        )}
      </SelectionModal>
    </>
  )
}
